/**
 * Master Data API Endpoints
 * Framework-agnostic endpoint definitions for all master data modules
 */

/**
 * Region API Endpoints
 */
export const REGION_ENDPOINTS = {
  list: 'region/get-region',
  create: 'region/create-region',
  update: 'region/update-region',
  delete: 'region/delete-region',
  import: 'region/import-region',
} as const;

/**
 * State API Endpoints
 */
export const STATE_ENDPOINTS = {
  list: 'state/get-state',
  create: 'state/create-state',
  update: 'state/update-state',
  delete: 'state/delete-state',
  import: 'state/import-state',
} as const;

/**
 * City API Endpoints
 */
export const CITY_ENDPOINTS = {
  list: 'city/get-city',
  create: 'city/create-city',
  update: 'city/update-city',
  delete: 'city/delete-city',
  import: 'city/import-city',
} as const;

/**
 * Brand Category API Endpoints
 */
export const BRAND_CATEGORY_ENDPOINTS = {
  list: 'brands/get-brand-categories',
  create: 'brands/create-brand-category',
  update: 'brands/update-brand-category',
  delete: 'brands/update-brand-category', // Uses same endpoint
} as const;

/**
 * Brand API Endpoints
 */
export const BRAND_ENDPOINTS = {
  list: 'brands/get-brand',
  create: 'brands/create-brand',
  update: 'brands/update-brand',
  delete: 'brands/delete-brand',
} as const;

/**
 * Language API Endpoints
 */
export const LANGUAGE_ENDPOINTS = {
  list: 'language/get-languages',
  create: 'language/create-language',
  update: 'language/update-language',
  delete: 'language/delete-language',
} as const;

/**
 * Unit API Endpoints
 */
export const UNIT_ENDPOINTS = {
  list: 'units/get-units',
  create: 'units/create-unit',
  update: 'units/update-unit',
  delete: 'units/update-unit', // Uses same endpoint with id param
} as const;

/**
 * Banner API Endpoints
 */
export const BANNER_ENDPOINTS = {
  list: 'banner/get-banner',
  create: 'banner/create-banner',
  update: 'banner/update-banner',
  delete: 'banner/delete-banner',
} as const;

/**
 * Vendor API Endpoints
 */
export const VENDOR_ENDPOINTS = {
  list: 'vendor/get-vendors', // Adjust based on actual API
} as const;

/**
 * Reports Login Credential API Endpoints
 * Note: This might use existing region/state/city endpoints
 */
export const REPORTS_CREDENTIAL_ENDPOINTS = {
  // Add specific endpoints if available
  regions: REGION_ENDPOINTS.list,
  states: STATE_ENDPOINTS.list,
  cities: CITY_ENDPOINTS.list,
} as const;

/**
 * Frequency API Endpoints
 */
export const FREQUENCY_ENDPOINTS = {
  list: 'frequency/get-frequency',
  create: 'frequency/create-frequency',
  update: 'frequency/update-frequency',
  delete: 'frequency/update-frequency',
} as const;

/**
 * Cycle API Endpoints
 */
export const CYCLE_ENDPOINTS = {
  list: 'frequency/get-cycle',
  create: 'frequency/create-cycle',
  update: 'frequency/update-cycle',
  delete: 'frequency/delete-cycle',
} as const;

/**
 * Quarter API Endpoints
 */
export const QUARTER_ENDPOINTS = {
  list: 'frequency/get-quarter',
  create: 'frequency/create-quarter',
  update: 'frequency/update-quarter',
  delete: 'frequency/delete-quarter',
} as const;

/**
 * Delivery Partner API Endpoints
 */
export const DELIVERY_PARTNER_ENDPOINTS = {
  list: 'delivery-partner/get-partner',
  create: 'delivery-partner/create-partner',
  update: 'delivery-partner/update-partner',
  delete: 'delivery-partner/delete-partner',
} as const;

/**
 * Combined Master Data Endpoints
 * For easy access to all endpoints
 */
export const MASTER_DATA_ENDPOINTS = {
  region: REGION_ENDPOINTS,
  state: STATE_ENDPOINTS,
  city: CITY_ENDPOINTS,
  brandCategory: BRAND_CATEGORY_ENDPOINTS,
  brand: BRAND_ENDPOINTS,
  language: LANGUAGE_ENDPOINTS,
  unit: UNIT_ENDPOINTS,
  banner: BANNER_ENDPOINTS,
  vendor: VENDOR_ENDPOINTS,
  reportsCredential: REPORTS_CREDENTIAL_ENDPOINTS,
  frequency: FREQUENCY_ENDPOINTS,
  cycle: CYCLE_ENDPOINTS,
  quarter: QUARTER_ENDPOINTS,
  deliveryPartner: DELIVERY_PARTNER_ENDPOINTS,
} as const;
